import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { AlertCircle, AlertTriangle } from "lucide-react";
import SparklesGradientIcon from "@/assets/icons/SparklesGradientIcon";
import { useEffect, useMemo, useRef, useState } from "react";
import RecordingEntryDisplay from "@/modules/common/components/audio/RecordingEntryDisplay";
import { useAudioPlayback } from "@/modules/common/hooks/audio";
import {
  generateWaveformFromUrl,
  generateFallbackWaveform,
  WAVEFORM_BAR_COUNT,
  WaveformResult,
} from "@/modules/common/utils/audio.utils";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import { getZoomCallDetail } from "@/modules/lead-page/api/lead-page.v2.api";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";
import ComprehensiveAnalysis from "@/modules/zoom-calls/components/comprehensive-analysis";
import NextActionsCard from "@/modules/zoom-calls/components/next-actions-card";
import SegmentedTabs from "@/modules/zoom-calls/components/segmented-tabs";
import ZoomCallAiStatusInlineRow from "@/modules/zoom-calls/components/zoom-call-ai-status-inline-row";
import {
  ZoomCallAiStatusData,
  isStructuredExtractedFields,
} from "@/modules/zoom-calls/types";
import {
  plainHttp,
  parseTranscription,
  parseTimeToSeconds,
  TranscriptRow,
  KEY_POINTS_PREVIEW,
} from "@/modules/zoom-calls/utils/transcript";
import { compositeLeadNanoId } from "@/modules/zoom-calls/utils/open-lead";
import usePassport from "@/modules/common/hooks/passport";
import { PermissionsEnum, PermissionLevel } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

type ZoomCallDetailTab = "overview" | "analysis" | "transcript";

interface ZoomCallDetailBodyProps {
  zoomCallUid: string;
  activityItem: ActivityItem;
  showAgentFooter?: boolean;
  /** Fired when the user switches to a different Overview / Analysis / Transcript tab. */
  onTabChange?: (tab: ZoomCallDetailTab) => void;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
};

const formatLabel = (val: string) =>
  val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* -------------------------------------------------------------------------- */
/*  Main component                                                            */
/* -------------------------------------------------------------------------- */

const ZoomCallDetailBody = ({
  zoomCallUid,
  activityItem,
  showAgentFooter = true,
  onTabChange,
}: ZoomCallDetailBodyProps) => {
  const theme = useTheme();
  const { permissions: currentUserPermissions } = usePassport();
  const canViewAnalysis =
    userHasPermission(
      { name: PermissionsEnum.ZOOM_CALLS_ADMIN, level: PermissionLevel.READ },
      currentUserPermissions,
    ) ||
    userHasPermission(
      { name: PermissionsEnum.ZOOM_CALL_ANALYSIS, level: PermissionLevel.READ },
      currentUserPermissions,
    );

  // Audio playback
  const {
    togglePlay,
    isPlaying,
    getCurrentTimeForId,
    getDurationForId,
    isLoading: audioLoading,
    getErrorForId,
    playingId,
    loadedId,
    seekTo,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayback();

  const [waveform, setWaveform] = useState<WaveformResult | null>(null);
  const [keyPointsExpanded, setKeyPointsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<ZoomCallDetailTab>("overview");

  const handleTabChange = (value: string) => {
    const tab = value as ZoomCallDetailTab;
    if (tab === activeTab) return;
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  // 1. Fetch zoom call detail from backend
  const {
    data: callDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    refetch,
  } = useQuery({
    queryKey: ["zoom-call-detail", zoomCallUid],
    queryFn: () => getZoomCallDetail(zoomCallUid),
    enabled: !!zoomCallUid,
    staleTime: 5 * 60 * 1000,
  });

  const recordingUrl = callDetail?.presignedRecordingUrl;

  const transcriptionUrl = callDetail?.presignedTranscriptionUrl;

  // 2. Fetch transcription text from presigned URL
  const {
    data: transcriptionText,
    isError: isTranscriptionError,
    fetchStatus: transcriptionFetchStatus,
  } = useQuery({
    queryKey: ["zoom-call-detail-transcription", transcriptionUrl],
    queryFn: async () => {
      const { data } = await plainHttp.get<string>(transcriptionUrl!, {
        responseType: "text",
      });
      return data;
    },
    enabled: !!transcriptionUrl && !isDetailLoading,
    staleTime: 30 * 60 * 1000,
  });

  // Generate waveform when recording URL is available
  useEffect(() => {
    if (recordingUrl) {
      generateWaveformFromUrl(recordingUrl, WAVEFORM_BAR_COUNT).then(
        setWaveform,
      );
    }
  }, [recordingUrl]);

  const playbackId = `zoom-detail-${zoomCallUid}`;

  const recordingEntry: RecordingEntry | null = recordingUrl
    ? {
        id: playbackId,
        audioBlob: null,
        audioUrl: recordingUrl,
        duration:
          getDurationForId(playbackId) > 0
            ? getDurationForId(playbackId)
            : waveform?.duration || 0,
        waveformData:
          waveform?.waveformData ||
          generateFallbackWaveform(WAVEFORM_BAR_COUNT),
      }
    : null;

  // Parse transcription
  const parsedTranscript = useMemo(
    () => (transcriptionText ? parseTranscription(transcriptionText) : null),
    [transcriptionText],
  );

  // Analysis data
  const sentiment = callDetail?.sentiment || activityItem.zoomCallSentiment;
  const summary = callDetail?.summary || activityItem.zoomCallSummary;
  const flags = callDetail?.flags as Record<string, any> | null | undefined;
  const callOutcome = flags?.call_outcome as string | undefined;
  const redFlags = flags?.red_flags as string[] | undefined;
  const requiresEscalation = flags?.requires_escalation as boolean | undefined;
  const escalationReason = flags?.escalation_reason as string | undefined;
  const speakerMapping = flags?.speaker_mapping as
    | Record<string, string>
    | undefined;

  // Structured extracted-fields payload (next-action rows source).
  const structuredExtracted = isStructuredExtractedFields(
    callDetail?.extractedFieldsV2,
  )
    ? (callDetail?.extractedFieldsV2 ?? null)
    : isStructuredExtractedFields(callDetail?.extractedFields)
      ? (callDetail?.extractedFields ?? null)
      : null;

  const keyPoints = useMemo<string[]>(
    () =>
      (summary ?? []).filter(
        (s): s is string => typeof s === "string" && s.trim().length > 0,
      ),
    [summary],
  );

  // Sentiment chips to display
  const sentimentChips = useMemo(() => {
    const chips: Array<{ label: string; isArc?: boolean }> = [];
    if (sentiment?.overall_client) {
      chips.push({ label: `Client ${sentiment.overall_client}` });
    }
    if (sentiment?.overall_agent) {
      chips.push({ label: `Agent ${sentiment.overall_agent}` });
    }
    if (sentiment?.arc) {
      chips.push({ label: sentiment.arc.replace(/_/g, " "), isArc: true });
    }
    return chips;
  }, [sentiment]);

  const isLoading = isDetailLoading;

  // Agent info
  const agentName = callDetail?.agentName || activityItem.actorName;
  const customerName = callDetail?.customer?.name;
  const callDate = callDetail?.createdAt || activityItem.createdAt;
  const formattedDate = callDate
    ? format(parseISO(callDate), "d MMM yyyy, h:mm a")
    : "";

  // Map raw transcript speaker labels to roles using speaker_mapping (sourced
  // from zoom_call_analyses.extra.speaker_mapping on the backend), then resolve
  // roles ("Agent"/"Client") and the unknown-speaker fallback to real names.
  const mappedTranscript = useMemo(() => {
    if (!parsedTranscript) return null;
    return parsedTranscript.map((msg) => {
      const key = msg.rawSpeaker ?? msg.speaker;
      const role = key ? speakerMapping?.[key] : undefined;
      let speaker = msg.speaker;
      if (role === "Agent") speaker = agentName || "Agent";
      else if (role === "Client") speaker = customerName || "Customer";
      else if (role) speaker = role;
      else if (msg.speaker === "Customer") speaker = customerName || "Customer";
      return { ...msg, speaker };
    });
  }, [parsedTranscript, speakerMapping, agentName, customerName]);

  // Pre-compute each transcript message's start time in seconds
  const transcriptStartTimes = useMemo(
    () => (mappedTranscript ?? []).map((m) => parseTimeToSeconds(m.time)),
    [mappedTranscript],
  );

  // Find the active transcript index based on current playback time
  const currentPlaybackTime = getCurrentTimeForId(playbackId);
  const activeTranscriptIdx = useMemo(() => {
    if (!isPlaying(playbackId) || transcriptStartTimes.length === 0) return -1;
    let idx = -1;
    for (let i = 0; i < transcriptStartTimes.length; i++) {
      if (transcriptStartTimes[i] <= currentPlaybackTime) idx = i;
      else break;
    }
    return idx;
  }, [currentPlaybackTime, transcriptStartTimes, isPlaying, playbackId]);

  // Auto-scroll the active transcript row into view
  const transcriptRowsRef = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    if (activeTranscriptIdx < 0) return;
    const el = transcriptRowsRef.current[activeTranscriptIdx];
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeTranscriptIdx]);

  // Detect whether the sticky audio player is currently stuck (scrolled past
  // its natural position) so we can apply drawer-style wrapper only on scroll.
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const [isPlayerStuck, setIsPlayerStuck] = useState(false);
  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsPlayerStuck(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [recordingEntry]);

  /** Figma: roundness/xl = 12px */
  const cardRadius = "12px";

  /* ----- Shared content --------------------------------------------------- */

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.nawy.spacing.sm,
            py: theme.nawy.spacing.xl,
          }}
        >
          <CircularProgress size={28} />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            Loading call details...
          </Typography>
        </Box>
      );
    }

    if (isDetailError) {
      return (
        <Box
          role="alert"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            py: theme.nawy.spacing.xl,
            textAlign: "center",
          }}
        >
          <AlertTriangle
            size={28}
            color={theme.nawy.colors.semantic.indicator.red}
            aria-hidden
          />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.semibold,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            Couldn&apos;t load call details
          </Typography>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
              maxWidth: 280,
            }}
          >
            Something went wrong. Try again in a moment.
          </Typography>
          <Typography
            component="button"
            onClick={() => refetch()}
            sx={{
              mt: theme.nawy.spacing.xs,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              ...theme.nawy.typography.english.variants.body.sm.semibold,
              color: theme.nawy.colors.semantic.indicator.navy,
              textDecoration: "underline",
              "&:hover": { opacity: 0.8 },
            }}
          >
            Try again
          </Typography>
        </Box>
      );
    }

    const tabsStrip = (
      <Box>
        <SegmentedTabs
          value={activeTab}
          onChange={handleTabChange}
          options={[
            { value: "overview", label: "Overview" },
            ...(canViewAnalysis
              ? [{ value: "analysis", label: "Analysis" }]
              : []),
            { value: "transcript", label: "Transcript" },
          ]}
        />
      </Box>
    );

    if (canViewAnalysis && activeTab === "analysis") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          }}
        >
          {tabsStrip}
          <ComprehensiveAnalysis
            flags={flags}
            sentiment={sentiment}
            extractedFields={callDetail?.extractedFields}
            extractedFieldsV2={callDetail?.extractedFieldsV2}
            showExtractedFields={false}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.sm,
        }}
      >
        {tabsStrip}

        {/* ── Summary card ── */}
        {activeTab === "overview" &&
          (sentimentChips.length > 0 || keyPoints.length > 0) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                px: theme.nawy.spacing.sm,
                py: theme.nawy.spacing.xs,
                borderRadius: cardRadius,
                backgroundColor: theme.nawy.colors.primitives.white[200],
              }}
            >
              {/* "Call summary" label + sparkles icon */}
              <Box
                sx={{
                  display: "flex",
                  gap: "4px",
                  height: 20,
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 400,
                    lineHeight: "16px",
                    color: theme.nawy.colors.semantic.text.tertiary,
                    whiteSpace: "nowrap",
                  }}
                >
                  Call summary
                </Typography>
                <SparklesGradientIcon size={16} />
              </Box>

              {/* Sentiment */}
              {sentimentChips.length > 0 && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
                >
                  {/* Client / Agent chips */}
                  {sentimentChips.some((c) => !c.isArc) && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        flexWrap: "wrap",
                        minWidth: 0,
                        maxWidth: "100%",
                      }}
                    >
                      {sentimentChips
                        .filter((c) => !c.isArc)
                        .map((chip, idx) => (
                          <Chip
                            key={idx}
                            label={chip.label}
                            size="small"
                            sx={{
                              fontSize: 12,
                              fontWeight: 400,
                              lineHeight: "18px",
                              color: theme.nawy.colors.semantic.text.primary,
                              backgroundColor:
                                theme.nawy.colors.semantic.layer.primary,
                              borderRadius: "4px",
                              height: "auto",
                              maxWidth: "100%",
                              "& .MuiChip-label": {
                                px: theme.nawy.spacing.xs,
                                py: "2px",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                              },
                            }}
                          />
                        ))}
                    </Box>
                  )}
                  {/* Arc — plain text, no bullet */}
                  {sentimentChips
                    .filter((c) => c.isArc)
                    .map((chip, idx) => (
                      <Typography
                        key={idx}
                        sx={{
                          ...theme.nawy.typography.english.variants.body.md
                            .regular,
                          color: theme.nawy.colors.semantic.text.primary,
                        }}
                      >
                        {chip.label}
                      </Typography>
                    ))}
                </Box>
              )}

              {/* Key points list */}
              {keyPoints.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: "20px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {(keyPointsExpanded
                      ? keyPoints
                      : keyPoints.slice(0, KEY_POINTS_PREVIEW)
                    ).map((point, idx) => (
                      <Typography
                        key={idx}
                        component="li"
                        sx={{
                          ...theme.nawy.typography.english.variants.body.md
                            .regular,
                          color: theme.nawy.colors.semantic.text.primary,
                        }}
                      >
                        {point}
                      </Typography>
                    ))}
                  </Box>
                  {keyPoints.length > KEY_POINTS_PREVIEW && (
                    <Button
                      onClick={() => setKeyPointsExpanded((prev) => !prev)}
                      sx={{
                        mt: 0.5,
                        p: 0,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        color: theme.nawy.colors.semantic.text.accent,
                        minWidth: "auto",
                        "&:hover": { background: "transparent" },
                      }}
                    >
                      {keyPointsExpanded
                        ? "View less"
                        : `View more (${keyPoints.length - KEY_POINTS_PREVIEW} more)`}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

        {/* ── Next action card ── */}
        {activeTab === "overview" && callDetail?.canViewNextActions && (
          <NextActionsCard
            data={structuredExtracted}
            callUid={callDetail.uid}
            tasksCreatedAt={callDetail.tasksCreatedAt ?? null}
            lead={
              callDetail.journey && callDetail.customer
                ? {
                    uid: callDetail.journey.uid,
                    /* Task linked entities key leads by the composite
                       {id}-{nanoId}; the bare journey nanoId is only the
                       short suffix. */
                    nanoId: compositeLeadNanoId(
                      callDetail.journey.id,
                      callDetail.journey.nanoId,
                    ),
                    name: callDetail.customer.name,
                  }
                : null
            }
            assignee={
              callDetail.agentUid
                ? {
                    uid: callDetail.agentUid,
                    name: callDetail.agentName ?? callDetail.agentEmail,
                  }
                : null
            }
          />
        )}

        {/* ── Call outcome card ── */}
        {activeTab === "overview" && callOutcome && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              px: "12px",
              py: theme.nawy.spacing.sm,
              borderRadius: cardRadius,
              backgroundColor: theme.nawy.colors.primitives.white[200],
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              Call outcome
            </Typography>
            <Chip
              label={formatLabel(callOutcome)}
              size="small"
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                backgroundColor: "#015c9a",
                color: "#fff",
                borderRadius: "4px",
                height: "auto",
                "& .MuiChip-label": { px: theme.nawy.spacing.xs, py: "2px" },
              }}
            />
          </Box>
        )}

        {/* ── AI Suggested Status ── (self-hides when there is no suggestion) */}
        {activeTab === "overview" && callDetail && (
          <ZoomCallAiStatusInlineRow
            aiStatus={
              (callDetail.aiStatus ?? null) as ZoomCallAiStatusData | null
            }
            variant="full"
          />
        )}

        {/* ── Escalation banner ── */}
        {activeTab === "overview" && requiresEscalation && (
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              alignItems: "center",
              p: "12px",
              borderRadius: cardRadius,
              backgroundColor: "#feeceb",
            }}
          >
            <AlertCircle
              size={16}
              color={theme.nawy.colors.semantic.indicator.red}
              style={{ flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 400,
                lineHeight: "18px",
                color: theme.nawy.colors.semantic.text.primary,
              }}
            >
              {escalationReason
                ? `Escalation ${escalationReason.charAt(0).toLowerCase()}${escalationReason.slice(1)}`
                : "Requires escalation"}
            </Typography>
          </Box>
        )}

        {/* ── Red flags card ── */}
        {activeTab === "overview" && redFlags && redFlags.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              p: "12px",
              borderRadius: cardRadius,
              backgroundColor: "#fff6f0",
            }}
          >
            {redFlags.map((flag, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <AlertCircle
                  size={16}
                  color={
                    theme.nawy.colors.semantic.indicator.orange ?? "#b34b00"
                  }
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {flag}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Extracted fields (Client request) ──
             Not analysis-gated: the client's stated requirements are visible to
             anyone who can open the call, and `showCallInsights={false}` keeps
             the analysis-only cards (details, sentiment arc, talk ratio, notes)
             out of this instance. */}
        {activeTab === "overview" && (
          <ComprehensiveAnalysis
            extractedFields={callDetail?.extractedFields}
            extractedFieldsV2={callDetail?.extractedFieldsV2}
            showCallInsights={false}
          />
        )}

        {/* ── Transcript card (audio + transcript rows) ── */}
        {activeTab === "transcript" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.xs,
              px: theme.nawy.spacing.sm,
              py: theme.nawy.spacing.xs,
              borderRadius: cardRadius,
              backgroundColor: theme.nawy.colors.primitives.white[200],
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              Transcript
            </Typography>

            {/* Sentinel — used to detect when sticky player is stuck */}
            {recordingEntry && (
              <Box ref={stickySentinelRef} sx={{ height: 0 }} />
            )}

            {/* Audio player — sticky to top of scroll container */}
            {recordingEntry && (
              <Box
                sx={{
                  position: "sticky",
                  top: `-${theme.nawy.spacing.md}`,
                  zIndex: 2,
                  backgroundColor: isPlayerStuck
                    ? theme.nawy.colors.semantic.layer.primary
                    : "transparent",
                  mx: isPlayerStuck
                    ? `calc(-${theme.nawy.spacing.sm} - ${theme.nawy.spacing.md})`
                    : 0,
                  px: isPlayerStuck
                    ? `calc(${theme.nawy.spacing.sm} + ${theme.nawy.spacing.md})`
                    : 0,
                  py: isPlayerStuck ? theme.nawy.spacing.sm : 0,
                  borderBottom: isPlayerStuck
                    ? `1px solid ${theme.nawy.colors.primitives.navy[50]}`
                    : "none",
                  transition: "background-color 0.15s",
                }}
              >
                <RecordingEntryDisplay
                  isActivity
                  recording={recordingEntry}
                  isPlaying={isPlaying(playbackId)}
                  currentPlaybackTime={getCurrentTimeForId(playbackId)}
                  onTogglePlay={() => togglePlay(playbackId, recordingUrl!)}
                  theme={theme}
                  isLoading={
                    audioLoading &&
                    playingId === null &&
                    loadedId !== playbackId
                  }
                  error={getErrorForId(playbackId)}
                  onSeek={(time) => seekTo(playbackId, time)}
                  playbackRate={playbackRate}
                  onPlaybackRateChange={setPlaybackRate}
                />
              </Box>
            )}

            {/* Transcript rows */}
            {transcriptionFetchStatus === "fetching" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: theme.nawy.spacing.lg,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {isTranscriptionError &&
              transcriptionFetchStatus !== "fetching" && (
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                    color: theme.nawy.colors.semantic.text.tertiary,
                    fontStyle: "italic",
                    textAlign: "center",
                    py: theme.nawy.spacing.lg,
                  }}
                >
                  Failed to load transcription.
                </Typography>
              )}

            {transcriptionFetchStatus !== "fetching" &&
              !isTranscriptionError &&
              !transcriptionText &&
              !transcriptionUrl && (
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                    color: theme.nawy.colors.semantic.text.tertiary,
                    fontStyle: "italic",
                    textAlign: "center",
                    py: theme.nawy.spacing.lg,
                  }}
                >
                  No transcription available for this call.
                </Typography>
              )}

            {transcriptionFetchStatus !== "fetching" &&
              !isTranscriptionError &&
              transcriptionText && (
                <>
                  {mappedTranscript ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: theme.nawy.spacing.xs,
                      }}
                    >
                      {mappedTranscript.map((msg, idx) => (
                        <TranscriptRow
                          key={idx}
                          msg={msg}
                          isActive={idx === activeTranscriptIdx}
                          rowRef={(el) => {
                            transcriptRowsRef.current[idx] = el;
                          }}
                          onClick={
                            recordingEntry
                              ? () => {
                                  const seconds = transcriptStartTimes[idx];
                                  if (loadedId !== playbackId) {
                                    togglePlay(playbackId, recordingUrl!);
                                  }
                                  seekTo(playbackId, seconds);
                                }
                              : undefined
                          }
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box
                      component="pre"
                      sx={{
                        ...theme.nawy.typography.english.variants.body.md
                          .regular,
                        color: theme.nawy.colors.semantic.text.primary,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        margin: 0,
                        fontFamily: "inherit",
                      }}
                    >
                      {transcriptionText}
                    </Box>
                  )}
                </>
              )}
          </Box>
        )}

        {/* ── Agent footer (inline, not sticky) ── */}
        {showAgentFooter && activeTab === "overview" && agentName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.nawy.spacing.xs,
            }}
          >
            {/* Monogram — 24px circle */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.nawy.colors.semantic.indicator.navyLight,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: theme.nawy.colors.semantic.indicator.navy,
                }}
              >
                {getInitials(agentName).charAt(0)}
              </Typography>
            </Box>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
              }}
            >
              {agentName} called lead{formattedDate ? ` ${formattedDate}` : ""}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return renderContent();
};

export default ZoomCallDetailBody;
